import { useQuery } from '@tanstack/react-query';
import { subMonths } from 'date-fns';
import { useMemo } from 'react';
import supabase from '@/api/supabase';
import {
  ServiceData,
  TaskHistoryData,
  ServiceIncomeData,
  EmployeeIncomeData,
} from '@/components/payroll_tsx/AnalyticsBoard/types';
import {
  fetchTaskHistoryRange,
  getMonthDateRange,
  sumIncomeUpToDate,
} from '@/lib/payrollAnalytics';

/**
 * Hook for fetching and processing payroll analytics data
 */
export const usePayrollAnalyticsBoard = (selectedYear: number, selectedMonth: number) => {
  const { start: currentStart, end: currentEnd } = getMonthDateRange(selectedYear, selectedMonth);
  const prevMonthDate = subMonths(new Date(selectedYear, selectedMonth, 1), 1);
  const { start: prevStart, end: prevEnd } = getMonthDateRange(
    prevMonthDate.getFullYear(),
    prevMonthDate.getMonth()
  );

  const { data: services } = useQuery({
    queryKey: ['payroll-services'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('services')
        .select('id, serTitle')
        .order('serTitle');

      if (error) throw error;
      return (data || []) as ServiceData[];
    },
  });

  const {
    data: currentMonthData,
    isLoading: isCurrentMonthLoading,
    isError: isCurrentMonthError,
  } = useQuery({
    queryKey: ['payroll-currentMonth', currentStart, currentEnd],
    queryFn: async () =>
      fetchTaskHistoryRange(currentStart, currentEnd, '*') as Promise<TaskHistoryData[]>,
    enabled: !!services,
  });

  const serviceIncomeData = useMemo<ServiceIncomeData[]>(() => {
    if (!services || !currentMonthData) return [];

    const serviceIncomes: Record<string, { income: number; count: number }> = {};
    services.forEach(service => {
      if (service.serTitle) {
        serviceIncomes[service.serTitle] = { income: 0, count: 0 };
      }
    });

    currentMonthData.forEach(row => {
      for (let i = 1; i <= 20; i++) {
        const serviceKey = `t${i}` as keyof TaskHistoryData;
        const serviceData = row[serviceKey];
        if (!serviceData) continue;

        try {
          const parsedData =
            typeof serviceData === 'string' ? JSON.parse(serviceData) : serviceData;

          const { serTitle, serAmount } = parsedData;
          if (serTitle && typeof serAmount === 'number' && serviceIncomes[serTitle]) {
            serviceIncomes[serTitle].income += serAmount;
            serviceIncomes[serTitle].count += 1;
          }
        } catch (error) {
          console.debug(`Invalid service data in ${serviceKey}:`, serviceData, error);
        }
      }
    });

    const totalIncome = Object.values(serviceIncomes).reduce(
      (sum, service) => sum + service.income,
      0
    );

    return Object.entries(serviceIncomes)
      .map(([name, data]): ServiceIncomeData => ({
        name,
        value: Math.round(data.income * 100) / 100,
        count: data.count,
        percent: totalIncome ? Math.round((data.income / totalIncome) * 100 * 10) / 10 : 0,
      }))
      .filter(item => item.name && !isNaN(item.value) && item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [services, currentMonthData]);

  const {
    data: employeeData = [],
    isLoading: isEmployeeLoading,
    isError: isEmployeeError,
  } = useQuery({
    queryKey: ['payroll-employeeIncome', selectedYear, selectedMonth],
    queryFn: async () => {
      const taskData = (await fetchTaskHistoryRange(
        currentStart,
        currentEnd,
        'income, empUserId, checkinTime'
      )) as unknown as Array<{ income?: number; empUserId: string }>;

      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('empUserId, empName')
        .eq('empStatus', true);

      if (empError) throw empError;

      const employeeMap = (employees ?? []).reduce<Record<string, string>>((acc, emp) => {
        acc[emp.empUserId] = emp.empName;
        return acc;
      }, {});

      const incomeByEmployee = taskData.reduce<Record<string, number>>((acc, task) => {
        const employeeName = employeeMap[task.empUserId] || 'Others';
        acc[employeeName] = (acc[employeeName] || 0) + (task.income ?? 0);
        return acc;
      }, {});

      return Object.entries(incomeByEmployee)
        .map(([name, value]): EmployeeIncomeData => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    },
  });

  const {
    data: prevMonthTotal = 0,
    isLoading: isPrevLoading,
    isError: isPrevError,
  } = useQuery({
    queryKey: ['payroll-employeeIncome-prev', prevStart, prevEnd],
    queryFn: async () => {
      const taskData = await fetchTaskHistoryRange(prevStart, prevEnd, 'income, checkinTime');
      return sumIncomeUpToDate(taskData, prevMonthDate.getFullYear(), prevEnd);
    },
  });

  const currentTotal = employeeData.reduce((sum, item) => sum + (item?.value || 0), 0);
  const percentageChange = prevMonthTotal
    ? ((currentTotal - prevMonthTotal) / prevMonthTotal) * 100
    : 0;

  return {
    serviceIncomeData,
    employeeData,
    currentTotal,
    prevTotal: prevMonthTotal,
    percentageChange,
    isLoading: isCurrentMonthLoading || isEmployeeLoading || isPrevLoading,
    isError: isCurrentMonthError || isEmployeeError || isPrevError,
  };
};
